import { useEffect, useState } from 'react';
import { toast } from '@heroui/react';
import { useVisitsStore, type VisitStatus } from '@/store/visitsSlice';
import { VISIT_STATUS_LABELS } from '@/lib/visitStatus';
import { apiFetch } from '@/lib/api';
import { downloadBlob } from '@/lib/download';
import type { Location, WorkerRow, VisitTemplate } from '@/lib/types';

const STATUS_OPTIONS: { id: string; label: string }[] = [
  { id: 'all', label: 'Все статусы' },
  ...(Object.keys(VISIT_STATUS_LABELS) as VisitStatus[]).map((id) => ({
    id,
    label: VISIT_STATUS_LABELS[id],
  })),
];

export interface VisitsImportResult {
  imported: number;
  errors: string[];
}

export function useVisitsPage() {
  const fetchVisits = useVisitsStore((s) => s.fetchVisits);
  const statusFilter = useVisitsStore((s) => s.statusFilter);
  const setStatusFilter = useVisitsStore((s) => s.setStatusFilter);
  const error = useVisitsStore((s) => s.error);

  const [isImportOpen, setImportOpen] = useState(false);
  const [importResult, setImportResult] = useState<VisitsImportResult | null>(null);
  const [isImporting, setIsImporting] = useState(false);

  // Создание визита: шаблоны и воркеры (у клиента списки уже скопированы на API)
  const [isCreateOpen, setCreateOpen] = useState(false);
  const [templates, setTemplates] = useState<VisitTemplate[]>([]);
  const [workers, setWorkers] = useState<WorkerRow[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  useEffect(() => {
    if (!isCreateOpen) return;
    setCreateError(null);
    setLocations([]);
    void Promise.all([
      apiFetch('/api/v1/admin/templates').then((res) => (res.ok ? res.json() : [])),
      // воркеров берём из /workers — общего списка юзеров больше нет
      apiFetch('/api/v1/admin/workers?limit=100').then((res) =>
        res.ok ? res.json() : { items: [] },
      ),
    ])
      .then(([templateList, workerData]: [VisitTemplate[], { items: WorkerRow[] }]) => {
        // Визиты создаются только из запущенных шаблонов (guard и на API)
        setTemplates(templateList.filter((t) => t.status === 'active'));
        setWorkers(workerData.items);
      })
      .catch(() => {
        setTemplates([]);
        setWorkers([]);
      });
  }, [isCreateOpen]);

  /**
   * Локации доступных для выбранного шаблона: у клиента — свои,
   * у персонала — локации клиента шаблона. Неактивные скрываем (см. спеку).
   */
  const fetchLocationsForTemplate = async (templateId: string) => {
    const template = templates.find((t) => t.id === templateId);
    if (!template) {
      setLocations([]);
      return;
    }
    const params = new URLSearchParams({ limit: '100', enabled: 'true' });
    if (template.clientId) params.set('client_id', template.clientId);
    try {
      const res = await apiFetch(`/api/v1/admin/locations/?${params.toString()}`);
      const data = res.ok ? ((await res.json()) as { items: Location[] }) : { items: [] };
      setLocations(data.items);
    } catch {
      setLocations([]);
    }
  };

  const handleOpenCreate = () => setCreateOpen(true);

  const handleCloseCreate = () => {
    setCreateOpen(false);
    setCreateError(null);
  };

  const handleCreateVisit = async (payload: {
    visit_template_id: string;
    user_id: string;
    planned_at: string;
    location_id: string;
  }): Promise<boolean> => {
    setIsCreating(true);
    setCreateError(null);
    try {
      const res = await apiFetch('/api/v1/admin/visits', {
        method: 'POST',
        body: JSON.stringify({
          visit_template_id: payload.visit_template_id,
          user_id: payload.user_id,
          planned_at: payload.planned_at || undefined,
          location_id: payload.location_id,
        }),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new Error(data?.error ?? `Ошибка запроса (${res.status})`);
      }
      toast.success('Визит создан');
      void fetchVisits();
      return true;
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Не удалось создать визит';
      setCreateError(message);
      return false;
    } finally {
      setIsCreating(false);
    }
  };

  useEffect(() => {
    void fetchVisits();
  }, [fetchVisits]);

  const selectValue = statusFilter ?? 'all';

  const handleStatusChange = (key: string | number | null) =>
    setStatusFilter(key === 'all' || key === null ? null : (String(key) as VisitStatus));

  const handleOpenImport = () => {
    setImportResult(null);
    setImportOpen(true);
  };

  const handleCloseImport = () => {
    setImportOpen(false);
    setImportResult(null);
  };

  // эндпоинт за Bearer-токеном, поэтому скачиваем blob и триггерим клик программно
  const handleDownloadSample = async () => {
    try {
      const res = await apiFetch('/api/v1/admin/visits/import/sample');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      downloadBlob(await res.blob(), 'visits-import-sample.xlsx');
    } catch (e) {
      console.error('Failed to download sample', e);
    }
  };

  const handleImport = async (file: File) => {
    setIsImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await apiFetch('/api/v1/admin/visits/import', {
        method: 'POST',
        body: formData,
      });

      if (!res.ok) {
        const data = (await res.json().catch(() => null)) as { error?: string } | null;
        const message = data?.error ?? `Ошибка запроса (${res.status})`;
        toast.danger(message);
        setImportResult({ imported: 0, errors: [message] });
        return;
      }

      const result = (await res.json()) as VisitsImportResult;
      void fetchVisits();
      // без построчных ошибок показывать нечего — закрываем модалку
      if (result.errors.length === 0) {
        setImportOpen(false);
        setImportResult(null);
        toast.success(`Импортировано визитов: ${result.imported}`);
        return;
      }
      setImportResult(result);
      toast.warning(`Импортировано: ${result.imported}, ошибок: ${result.errors.length}`, {
        description: 'Подробности — в окне импорта',
      });
    } catch {
      const message = 'Ошибка сети. Проверьте подключение.';
      toast.danger(message);
      setImportResult({ imported: 0, errors: [message] });
    } finally {
      setIsImporting(false);
    }
  };

  return {
    statusOptions: STATUS_OPTIONS,
    selectValue,
    handleStatusChange,
    error,
    isImportOpen,
    importResult,
    isImporting,
    handleOpenImport,
    handleCloseImport,
    handleDownloadSample,
    handleImport,
    isCreateOpen,
    templates,
    workers,
    locations,
    fetchLocationsForTemplate,
    isCreating,
    createError,
    handleOpenCreate,
    handleCloseCreate,
    handleCreateVisit,
  };
}
